/**
 * ProductBreadcrumbs
 * Controls the behaviour of each product breadcrumbs component instance.
 */
class ProductBreadcrumbs {

  /**
   * Elements for the product breadcrumbs component.
   * @type {Object}
   */
  elements = {};

  /**
   * All collections assigned to the product.
   * @type {ProductBreadcrumbsCollectionDTO[]}
   */
  collections = [];

  /**
   * The lowest level collection assigned to the product.
   * @type {ProductBreadcrumbsCollectionDTO}
   */
  leafCollection = null;

  /**
   * The collections that make up the breadcrumb trail.
   * @type {ProductBreadcrumbsCollectionDTO[]}
   */
  breadcrumbTrailCollections = [];

  /**
   * ProductBreadcrumbs constructor.
   * @param {jQuery} container - The jQuery element wrapping this breadcrumbs instance.
   */
  constructor(container) {
    this.loadElements(container);
    this.loadCollections();
    if(!this.loadLeafCollection()) {
      return;
    }
    this.buildBreadcrumbTrailCollections();
    this.render();
    this.elements.container.prop('hidden', false);
  }

  /**
   * Loads the elements for the product breadcrumbs component.
   * @param {jQuery} container - The container element for this component.
   */
  loadElements(container) {
    this.elements.container = container;
    this.elements.delimiter = container.find('.product-breadcrumbs__delimiter').clone();
    this.elements.productTitle = container.find('.product-breadcrumbs__product-title');
    this.elements.menu = jQuery('#site-header-nav nav > ul.navmenu');
  }

  /**
   * Loads the collections for the product breadcrumbs component.
   */
  loadCollections() {
    // Read the comma-separated collection handles from the container data attribute.
    const collectionHandles = this.elements.container.data('collection-handles').split(',');

    // Read the collection titles (joined with "|,|") and normalise each one for display.
    const collectionTitles = this.elements.container.data('collection-titles').split('|,|').map(
      (title) => this.normalizeCollectionTitle(title)
    );

    // Pair each handle with its matching title into a DTO, preserving order.
    this.collections = collectionHandles.map((handle, index) => new ProductBreadcrumbsCollectionDTO(handle, collectionTitles[index]));
  }

  /**
   * Loads the leaf collection for the product breadcrumbs component.
   * 
   * @returns {boolean} - True if the leaf collection was successfully loaded.
   */
  loadLeafCollection() {
    if(this.loadLeafCollectionFromReferrer()) {
      return true;
    }

    if(this.loadLeafCollectionFromProductDefaultCollection()) {
      return true;
    }

    if(this.loadLeafCollectionFromMenu()) {
      return true;
    }

    return false;
  }

  /**
   * Loads the leaf collection from the referrer.
   *
   * @returns {boolean} - True if the leaf collection was successfully loaded from the referrer.
   */
  loadLeafCollectionFromReferrer() {
    const collectionHandle = this.getCollectionHandleFromUrl(document.referrer);

    if (!collectionHandle) {
      return false;
    }

    // Match the referrer handle against an assigned product collection.
    const collection = this.collections.find(
      (assignedCollection) => assignedCollection.handle === collectionHandle
    );

    // No matching assigned collection means we cannot load a leaf from the referrer.
    if (!collection) {
      return false;
    }

    // Assign the matched collection as the leaf collection for this breadcrumbs instance.
    this.leafCollection = collection;

    // Leaf collection was successfully loaded from the referrer.
    return true;
  }

  /**
   * Loads the leaf collection from the current product's default collection.
   *
   * @returns {boolean} - True if the leaf collection was successfully loaded from the product default collection.
   */
  loadLeafCollectionFromProductDefaultCollection() {
    const collectionHandle = this.elements.container.data('default-collection-handle');
    const collectionTitle = this.elements.container.data('default-collection-title');

    // Both the default collection handle and title must be present.
    if (!collectionHandle || !collectionTitle) {
      return false;
    }

    // Assign a new collection object as the leaf collection for this breadcrumbs instance.
    this.leafCollection = new ProductBreadcrumbsCollectionDTO(
      collectionHandle,
      this.normalizeCollectionTitle(collectionTitle)
    );

    // Leaf collection was successfully loaded from the product default collection.
    return true;
  }

  /**
   * Loads the leaf collection from the menu.
   * Finds the deepest collection link in the menu whose collection is assigned to the product.
   *
   * @returns {boolean} - True if the leaf collection was successfully loaded from the menu.
   */
  loadLeafCollectionFromMenu() {
    const collectionLinks = [];

    // Get the deepest menu link for each assigned product collection.
    for (const collection of this.collections) {
      const collectionLink = this.getDeepestCollectionLinkFromMenu(collection.handle);

      if (!collectionLink) {
        continue;
      }

      collectionLinks.push(collectionLink);
    }

    // No matching menu collection means we cannot load a leaf from the menu.
    if (!collectionLinks.length) {
      return false;
    }

    // Sort links by depth so the deepest (most LI parents) comes first.
    collectionLinks.sort(
      (collectionLinkA, collectionLinkB) => {
        return collectionLinkB.parentsUntil(this.elements.menu, 'li').length - collectionLinkA.parentsUntil(this.elements.menu, 'li').length;
      }
    );

    const deepestCollectionLink = collectionLinks[0];
    const collectionHandle = this.getCollectionHandleFromUrl(this.getMenuLinkUrl(deepestCollectionLink));

    // Match the menu link handle against an assigned product collection.
    const collection = this.collections.find(
      (assignedCollection) => assignedCollection.handle === collectionHandle
    );

    // Assign the deepest matched collection as the leaf collection for this breadcrumbs instance.
    this.leafCollection = collection;

    // Leaf collection was successfully loaded from the menu.
    return true;
  }

  /**
   * Builds the collections that make up the breadcrumb trail.
   * Starts from the leaf collection, then walks up the menu hierarchy
   * collecting parent collection links towards the top of the menu.
   */
  buildBreadcrumbTrailCollections() {
    // Start the breadcrumb trail with the leaf collection.
    this.breadcrumbTrailCollections.push(this.leafCollection);

    // Find the deepest menu link for the leaf collection.
    const deepestLeafCollectionLink = this.getDeepestCollectionLinkFromMenu(this.leafCollection.handle);

    // If we cannot identify a menu item for the leaf collection, stop here.
    if (!deepestLeafCollectionLink) {
      return;
    }

    // Step backwards through parent menu items towards the top of the menu.
    const parentListItems = deepestLeafCollectionLink.parentsUntil(this.elements.menu, 'li');

    parentListItems.each(
      (index) => {
        // Skip the leaf collection's own list item.
        if (index === 0) {
          return;
        }

        const parentListItem = parentListItems.eq(index);

        // The first collection link in document order belongs to this menu level,
        // since the item's own link always appears before any nested submenu links.
        const parentCollectionLink = parentListItem.find('[href*="/collections/"], [data-href*="/collections/"]').first();

        if (!parentCollectionLink.length) {
          return;
        }

        const collectionHandle = this.getCollectionHandleFromUrl(this.getMenuLinkUrl(parentCollectionLink));

        if (!collectionHandle) {
          return;
        }

        // Prepend the parent collection so the trail builds towards the highest menu level.
        this.breadcrumbTrailCollections.unshift(
          new ProductBreadcrumbsCollectionDTO(
            collectionHandle,
            this.normalizeCollectionTitle(parentCollectionLink.text())
          )
        );
      }
    );
  }

  /**
   * Renders the product breadcrumbs items.
   */
  render() {
    for(const collection of this.breadcrumbTrailCollections) {
      const link = jQuery('<a href="/collections/' + collection.handle + '">' + collection.title + '</a>');
      link.insertBefore(this.elements.productTitle);
      const delimiter = this.elements.delimiter.clone();
      delimiter.insertBefore(this.elements.productTitle);
    }
  }

  /**
   * Helper methods below.
  */

  /**
   * Normalises a collection title for display.
   * Collection titles are longer forward-slash separated strings.
   * Only the final section should be displayed to the user.
   *
   * @param {string} title - The raw collection title.
   * @returns {string} - The normalised collection title.
   */
  normalizeCollectionTitle(title) {
    const titleParts = title.split('/');
    return titleParts[titleParts.length - 1].trim();
  }

  /**
   * Gets the URL from a menu link element.
   * Menu items may use a standard href, or a data-href (e.g. summary tags inside details).
   *
   * @param {jQuery} menuLink - The menu link element.
   * @returns {string} - The URL from the href or data-href attribute.
   */
  getMenuLinkUrl(menuLink) {
    return menuLink.attr('href') || menuLink.attr('data-href') || '';
  }

  /**
   * Gets the collection handle from a URL.
   * The segment directly after "collections" is treated as the collection handle.
   *
   * @param {string} url - The URL to extract the collection handle from.
   * @returns {string} - The collection handle, or an empty string if none found.
   */
  getCollectionHandleFromUrl(url) {
    const urlParts = url.split('/');

    if (!urlParts.includes('collections')) {
      return '';
    }

    return (urlParts[urlParts.indexOf('collections') + 1] || '').split('?')[0].split('#')[0];
  }

  /**
   * Gets the deepest collection link from the menu for a given collection handle.
   *
   * @param {string} collectionHandle - The collection handle to find in the menu.
   * @returns {jQuery|null} - The deepest matching collection link, or null if none found.
   */
  getDeepestCollectionLinkFromMenu(collectionHandle) {
    // Menu element must be present to find a collection link.
    if (!this.elements.menu.length) {
      return null;
    }

    let deepestCollectionLink = null;
    let deepestDepth = -1;

    // Find all menu links that point to a collection (href or data-href).
    const collectionLinks = this.elements.menu.find('[href*="/collections/"], [data-href*="/collections/"]');

    collectionLinks.each(
      (index) => {
        const collectionLink = collectionLinks.eq(index);
        const linkCollectionHandle = this.getCollectionHandleFromUrl(this.getMenuLinkUrl(collectionLink));

        // Only consider links that point to the specified collection.
        if (linkCollectionHandle !== collectionHandle) {
          return;
        }

        // Depth is based on how nested the link is within the menu structure.
        const depth = collectionLink.parentsUntil(this.elements.menu, 'li').length;

        // Keep the deepest matching collection link found so far.
        if (deepestDepth < depth) {
          deepestDepth = depth;
          deepestCollectionLink = collectionLink;
        }
      }
    );

    return deepestCollectionLink;
  }
}

class ProductBreadcrumbsCollectionDTO {
  handle = null;
  title = null;

  constructor(handle, title) {
    this.handle = handle;
    this.title = title;
  }
}

/**
 * Waits for required dependencies to be loaded before initializing product breadcrumbs.
 */
(async() => {
  // Wait for dependencies to load (poll every 10ms).
  await new Promise(
    (resolve) => {
      let interval = setInterval(
        () => {
          if (typeof jQuery == 'undefined') {
            return;
          }

          clearInterval(interval);
          resolve();
        },
        10
      )
    }
  );

  jQuery(document).ready(
    () => {
      const containers = jQuery('.product-breadcrumbs__container');

      containers.each(
        (index, container) => {
          new ProductBreadcrumbs(containers.eq(index));
        }
      );
    }
  );
})();
